import { useMemo, useCallback } from "react";
import { isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";

// Constants
import { orderSearchCategories } from "../../../constants";
import { checkIfClient, TABLE_MODE_OPTIONS, queryKeys } from "./constants";

// Hooks
import useDecodeToken from "../../../hooks/useDecodeToken";
import {
  useGlobalFilter,
  GLOBAL_FILTER_ACTION_CREATORS,
  useTableController,
  TABLE_ACTION_CREATORS,
  useSlowQueryDetection,
} from "./hooks";
import { useSSROrders, useSSRStatuses, useCSROrders } from "./services";

// Components
import withDocumentTitle from "../../withDocumentTitle";
import { RestrictedContent } from "../../Navbar/RestrictedContent";
import { CDKAlertNote2 } from "../../CDK/CDKSignupPage/CDKAlertNote2";
import StatusSummary from "./components/StatusSummary";

import TableToolbar from "./components/table/TableToolbar";
import ClientSideDataTable from "./components/table/ClientSideDataTable";
import ServerSideDataTable from "./components/table/ServerSideDataTable";
import moment from "moment";

function CustomErrorComponent({ error }) {
  if (isAxiosError(error)) {
    return (
      <div className="tw-text-red-600 tw-font-medium">
        <h2 className="tw-text-lg tw-font-semibold">
          An error occurred, Status: {error.response?.status || "Network Error"}
        </h2>
        <p>{error.response?.data?.message || error.message}</p>
      </div>
    );
  }

  return (
    <div className="tw-p-4 tw-bg-red-100 tw-text-red-700 tw-rounded">
      <h2 className="tw-text-lg tw-font-semibold">An error occurred</h2>
      <p>{error.message}</p>
    </div>
  );
}

function OrderHistoryPage({ mode, onChangeMode }) {
  const [decodedToken] = useDecodeToken();
  const isClient = useMemo(() => checkIfClient(decodedToken), [decodedToken]);

  const [globalFilterState, globalFilterDispatch] = useGlobalFilter();
  const [tableState, tableDispatch] = useTableController();
  const queryClient = useQueryClient();

  const {
    data: pageOrders,
    isFetching,
    isLoading,
    isError,
    error,
  } = useSSROrders({
    // Global filter params
    searchCategory: globalFilterState.searchBy,
    fromDate: globalFilterState.fromDate,
    toDate: globalFilterState.toDate,
    invoices: globalFilterState.invoices,
    barcodes: globalFilterState.barcodes,

    // Table params
    pageIndex: tableState.pageIndex,
    pageSize: tableState.pageSize,
    columnsFilter: tableState.columnsFilter,
    columnsSorting: tableState.columnsSorting,

    isServerSide: tableState.mode === TABLE_MODE_OPTIONS.SERVER,
  });

  const { isFetching: isFetchingStatus, isError: isErrorStatus } =
    useSSRStatuses({
      // Global filter params
      searchCategory: globalFilterState.searchBy,
      fromDate: globalFilterState.fromDate,
      toDate: globalFilterState.toDate,
      invoices: globalFilterState.invoices,
      barcodes: globalFilterState.barcodes,

      // Table params
      pageIndex: tableState.pageIndex,
      pageSize: tableState.pageSize,
      columnsFilter: tableState.columnsFilter,
      columnsSorting: tableState.columnsSorting,
      isServerSide: tableState.mode === TABLE_MODE_OPTIONS.SERVER,
    });
  const maxStatusFetchDateRange = 60;
  const isEnabledAllOrders =
    moment(globalFilterState.toDate).diff(
      moment(globalFilterState.fromDate),
      "days",
    ) <= maxStatusFetchDateRange;
  const {
    data: allOrders,
    isFetching: isFetchingAllOrders,
    isSuccess: isSuccessAllOrders,
    isError: isErrorAllOrders,
    error: errorAllOrders,
  } = useCSROrders({
    // Global filter params
    searchCategory: globalFilterState.searchBy,
    fromDate: globalFilterState.fromDate,
    toDate: globalFilterState.toDate,
    invoices: globalFilterState.invoices,
    barcodes: globalFilterState.barcodes,

    // Table params
    // isClientSide: tableState.mode === TABLE_MODE_OPTIONS.CLIENT,
    isClientSide: true,
    isEnabledAllOrders: isEnabledAllOrders,
  });

  const canFilterByStatus = useMemo(
    () =>
      isSuccessAllOrders &&
      !isFetchingAllOrders &&
      !isErrorAllOrders &&
      isEnabledAllOrders,
    [
      isSuccessAllOrders,
      isFetchingAllOrders,
      isErrorAllOrders,
      isEnabledAllOrders,
    ],
  );

  const isRequestTakingTooLongServerSidePagination =
    useSlowQueryDetection(isLoading);
  const isRequestTakingTooLongClientSidePagination =
    useSlowQueryDetection(isFetchingAllOrders);

  const handleOnPaginationChange = ({ pageIndex, pageSize }) => {
    tableDispatch(TABLE_ACTION_CREATORS.setPagination(pageIndex, pageSize));
  };

  const handleOnColumnsFilterChange = (columnsFilter) => {
    tableDispatch(TABLE_ACTION_CREATORS.setColumnsFilter(columnsFilter));
  };

  const handleOnColumnsSortingChange = (columnsSorting) => {
    tableDispatch(TABLE_ACTION_CREATORS.setColumnsSorting(columnsSorting));
  };

  const handleOnChangeTableMode = useCallback(
    (isClientSideRendering, statusColumnFilterValue) => {
      if (!isClientSideRendering && statusColumnFilterValue !== "") {
        tableDispatch(
          TABLE_ACTION_CREATORS.setMode(true, statusColumnFilterValue),
        );
      }

      if (
        isClientSideRendering &&
        (statusColumnFilterValue === undefined ||
          statusColumnFilterValue === "")
      ) {
        tableDispatch(
          TABLE_ACTION_CREATORS.setMode(false, statusColumnFilterValue),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSearch = (searchType, selections, fromDate, toDate) => {
    const baseParams = {
      searchBy: searchType,
      isClient,
    };
    if (searchType === orderSearchCategories.dateRange.value) {
      const clientSideDateRangeParams = {
        ...baseParams,
        fromDate,
        toDate,
      };

      const serverSideDateRangeParams = {
        ...baseParams,
        // pageIndex: tableState.pageIndex,
        pageIndex: 0,
        pageSize: tableState.pageSize,
        columnsFilter: tableState.columnsFilter,
        columnsSorting: tableState.columnsSorting,
        fromDate,
        toDate,
      };

      globalFilterDispatch(
        GLOBAL_FILTER_ACTION_CREATORS.setDateRange(fromDate, toDate),
      );

      // Client Side API Invalidation
      queryClient.invalidateQueries({
        queryKey: queryKeys.ALL_ORDERS_BY(
          searchType,
          clientSideDateRangeParams,
        ),
      });

      // Server Side API Invalidation
      queryClient.invalidateQueries({
        queryKey: queryKeys.DELIVERY_HISTORY_BY(
          searchType,
          serverSideDateRangeParams,
        ),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.STATUS_ORDERS_BY(
          searchType,
          serverSideDateRangeParams,
        ),
      });

      const shouldFetchAllStatuses =
        moment(toDate).diff(moment(fromDate), "days") <=
        maxStatusFetchDateRange;
      if (!shouldFetchAllStatuses) {
        tableDispatch(TABLE_ACTION_CREATORS.setMode(false, ""));
      }
    }

    if (searchType === orderSearchCategories.invoice.value) {
      const clientSideInvoiceParams = {
        ...baseParams,
        selected: selections,
      };

      const serverSideInvoiceParams = {
        ...baseParams,
        // pageIndex: tableState.pageIndex,
        pageIndex: 0,
        pageSize: tableState.pageSize,
        columnsFilter: tableState.columnsFilter,
        columnsSorting: tableState.columnsSorting,
        selected: selections,
      };

      globalFilterDispatch(
        GLOBAL_FILTER_ACTION_CREATORS.setInvoices(selections),
      );

      // Client Side API Invalidation
      queryClient.invalidateQueries({
        queryKey: queryKeys.ALL_ORDERS_BY(searchType, clientSideInvoiceParams),
      });

      // Server Side API Invalidation
      queryClient.invalidateQueries({
        queryKey: queryKeys.DELIVERY_HISTORY_BY(
          searchType,
          serverSideInvoiceParams,
        ),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.STATUS_ORDERS_BY(
          searchType,
          serverSideInvoiceParams,
        ),
      });
    }

    if (searchType === orderSearchCategories.barcode.value) {
      const clientSideBarcodeParams = {
        ...baseParams,
        selected: selections,
      };

      const serverSideBarcodeParams = {
        ...baseParams,
        // pageIndex: tableState.pageIndex,
        pageIndex: 0,
        pageSize: tableState.pageSize,
        columnsFilter: tableState.columnsFilter,
        columnsSorting: tableState.columnsSorting,
        selected: selections,
      };

      globalFilterDispatch(
        GLOBAL_FILTER_ACTION_CREATORS.setBarcodes(selections),
      );

      // Client Side API Invalidation
      queryClient.invalidateQueries({
        queryKey: queryKeys.ALL_ORDERS_BY(searchType, clientSideBarcodeParams),
      });

      // Server Side API Invalidation
      queryClient.invalidateQueries({
        queryKey: queryKeys.DELIVERY_HISTORY_BY(
          searchType,
          serverSideBarcodeParams,
        ),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.STATUS_ORDERS_BY(
          searchType,
          serverSideBarcodeParams,
        ),
      });
    }

    tableDispatch(
      TABLE_ACTION_CREATORS.setPageIndex(0), // Reset to first page on new search
    );
  };

  return (
    <div>
      <h1 className="tw-text-center tw-mb-4">Order History</h1>

      <RestrictedContent>
        {!isClient && (
          <div className="mx-auto mb-4" style={{ maxWidth: "1200px" }}>
            <CDKAlertNote2 message="If your order still says open after a while and you need to make a return, please use the manual return option found in the navigation bar above." />
          </div>
        )}
      </RestrictedContent>

      <TableToolbar
        isLoading={
          tableState.mode === TABLE_MODE_OPTIONS.CLIENT
            ? isFetchingAllOrders
            : isFetching
        }
        onSearch={handleSearch}
        mode={mode}
        onChangeMode={onChangeMode}
        doesOrderExist={allOrders?.deliveryList?.length > 0}
        orders={allOrders?.deliveryList || []}
      />

      <StatusSummary
        searchCategory={globalFilterState.searchBy}
        fromDate={globalFilterState.fromDate}
        toDate={globalFilterState.toDate}
        invoices={globalFilterState.invoices}
        barcodes={globalFilterState.barcodes}
        shouldFetchAllStatuses={isEnabledAllOrders}
        maxStatusFetchDateRange={maxStatusFetchDateRange}
      />

      {tableState.mode === TABLE_MODE_OPTIONS.CLIENT ? (
        <ClientSideDataTable
          orders={allOrders?.deliveryList || []}
          isLoading={isFetchingAllOrders}
          isError={isErrorAllOrders}
          customErrorMessage={<CustomErrorComponent error={errorAllOrders} />}
          isNetworkRequestTakingTooLong={
            isRequestTakingTooLongClientSidePagination
          }
          pageIndex={tableState?.pageIndex}
          pageSize={tableState?.pageSize}
          onPaginationChange={handleOnPaginationChange}
          // Filters props
          columnsFilter={tableState?.columnsFilter}
          onColumnsFilterChange={handleOnColumnsFilterChange}
          // Sorting props
          columnsSorting={tableState?.columnsSorting}
          onColumnsSortingChange={handleOnColumnsSortingChange}
          onChangePaginationMode={handleOnChangeTableMode}
        />
      ) : (
        <ServerSideDataTable
          orders={pageOrders?.deliveryList || []}
          totalRows={pageOrders?.total}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          customErrorMessage={<CustomErrorComponent error={error} />}
          isNetworkRequestTakingTooLong={
            isRequestTakingTooLongServerSidePagination
          }
          // Pagination props
          pageIndex={tableState?.pageIndex}
          pageSize={tableState?.pageSize}
          onPaginationChange={handleOnPaginationChange}
          // Filters props
          columnsFilter={tableState?.columnsFilter}
          onColumnsFilterChange={handleOnColumnsFilterChange}
          // Sorting props
          columnsSorting={tableState?.columnsSorting}
          onColumnsSortingChange={handleOnColumnsSortingChange}
          // cxt status props
          isFetchingStatus={isFetchingStatus}
          isErrorStatus={isErrorStatus}
          onChangePaginationMode={handleOnChangeTableMode}
          canFilterByStatus={canFilterByStatus}
          isFetchingAllOrders={isFetchingAllOrders}
          isEnabledAllOrders={isEnabledAllOrders}
          maxDateRangeForStatusFilter={maxStatusFetchDateRange}
        />
      )}
    </div>
  );
}

const OrderHistoryPageWithTitle = withDocumentTitle(OrderHistoryPage, {
  documentTitle: "Order History",
});

export default OrderHistoryPageWithTitle;
